import { api } from "../lib/api";

export type ContentFormat = "video" | "audio" | "text" | "cards";
export type ContentType = "video" | "article" | "podcast";

/**
 * Shape returned by era-api `GET /api/education-hub/articles`.
 * Mirrors `listForPatient`'s projection (id/title/clinicId/visibility/
 * personaMode/backend/publishedAt) — no jsonb blobs on the list endpoint.
 */
export interface EducationHubArticleSummary {
	id: string;
	title: string;
	clinicId: string;
	visibility: "public" | "clinic" | "private";
	personaMode: string | null;
	backend: string | null;
	publishedAt: string | null;
	hasVideo: boolean;
	hasPodcast: boolean;
	durationSeconds: number | null;
	imageUrl: string | null;
}

/**
 * Pull the patient's recommended/ed-hub articles from era-api.
 *
 * `api` is the shared axios instance — its request interceptor attaches the
 * Auth0 access token, and era-api's `validateAuth` lifts the `clinic_id` JWT
 * claim into `req.userClaims.clinic_id` so the route filter knows which
 * clinic's content (plus `visibility='public'` content) to return.
 *
 * Note on shape mapping: era-api's pipeline doesn't yet emit a description,
 * thumbnail, or duration — those fields land in the `pipeline_meta` jsonb
 * on the detail endpoint, not on the list projection. Until the pipeline
 * writes them, we synthesise sensible placeholders so the existing
 * `RecommendedContent` card layout still renders.
 */
export async function fetchRecommendedContent(): Promise<EducationContent[]> {
	const { data } = await api.get<{ articles: EducationHubArticleSummary[] }>(
		"/api/education-hub/articles",
		{ params: { limit: 20 } },
	);
	return data.articles.map(mapArticleToCard);
}

/**
 * Library page catalogue — audio/video content only. Same projection +
 * mapping as fetchRecommendedContent, but asks era-api for the AV subset.
 */
export async function fetchLibraryContent(): Promise<EducationContent[]> {
	const { data } = await api.get<{ articles: EducationHubArticleSummary[] }>(
		"/api/education-hub/articles",
		{ params: { media: "av", limit: 50 } },
	);
	return data.articles.map(mapArticleToCard);
}

/**
 * Shape returned by era-api `GET /api/education-hub/progress` (PR #153).
 * Server-persisted podcast playback progress, most-recently-updated first.
 */
export interface EducationProgressItem {
	contentId: string;
	title: string;
	contentType: "podcast" | "video";
	progress: number; // 0..1
	currentTime: number; // seconds
	updatedAt: string;
	imageUrl: string | null; // proxy/presigned hero image (resolve via resolveMediaUrl)
	durationSeconds: number | null; // total length for the "of 10:00" label
}

/** Media artefacts whose playback position era-api persists. */
export type ProgressContentType = "podcast" | "video";

/**
 * "Continue where you left off" — real, server-persisted playback progress for
 * one media type. Maps the API rows (`contentId`) onto the `ResumeItem` shape
 * (`id`); order is preserved (the API already returns most-recently-updated
 * first).
 */
export async function fetchEducationProgress(
	type: ProgressContentType,
): Promise<ResumeItem[]> {
	const { data } = await api.get<{ items: EducationProgressItem[] }>(
		"/api/education-hub/progress",
		{ params: { type } },
	);
	return data.items.map((p) => ({
		id: p.contentId,
		title: p.title,
		contentType: p.contentType,
		progress: p.progress,
		currentTime: p.currentTime,
		imageUrl: p.imageUrl ?? null,
		durationSeconds: p.durationSeconds ?? null,
	}));
}

/** Podcast slice of {@link fetchEducationProgress} (resume carousel). */
export async function fetchPodcastProgress(): Promise<ResumeItem[]> {
	return fetchEducationProgress("podcast");
}

/**
 * Upsert the current user's playback progress for one piece of media (per
 * user+content+type). Called on pause / ended / unmount and throttled during
 * playback.
 */
export async function saveEducationProgress(
	contentId: string,
	{
		progress,
		currentTime,
		contentType,
	}: {
		progress: number;
		currentTime: number;
		contentType: ProgressContentType;
	},
): Promise<void> {
	await api.put(`/api/education-hub/progress/${contentId}`, {
		progress,
		currentTime,
		contentType,
	});
}

/** Podcast slice of {@link saveEducationProgress}. */
export async function savePodcastProgress(
	contentId: string,
	{ progress, currentTime }: { progress: number; currentTime: number },
): Promise<void> {
	return saveEducationProgress(contentId, {
		progress,
		currentTime,
		contentType: "podcast",
	});
}

/**
 * Shape returned by era-api `GET /api/education-hub/articles/:id`.
 *
 * The detail endpoint returns the full row plus four presigned S3 URLs
 * (15-minute TTL). All four can be null while the prism pipeline is still
 * working on the row — the UI MUST handle the empty state per artefact.
 */
export interface EducationHubArticleDetail {
	id: string;
	title: string;
	clinicId: string;
	visibility: "public" | "clinic" | "private";
	personaMode: string | null;
	voiceAId: string | null;
	voiceBId: string | null;
	backend: string | null;
	classifierFallback: boolean | null;
	classifierMeta: Record<string, unknown> | null;
	articleUrl: string | null;
	cardsUrl: string | null;
	podcastUrl: string | null;
	videoUrl: string | null;
	publishedAt: string | null;
	createdAt: string;
	imageUrl: string | null;
}

export async function fetchArticleDetail(
	id: string,
): Promise<EducationHubArticleDetail> {
	const { data } = await api.get<EducationHubArticleDetail>(
		`/api/education-hub/articles/${id}`,
	);
	return data;
}

/**
 * Format a media length in seconds as `m:ss` for the library duration pill.
 * Returns the `"—"` sentinel for null/zero so LibraryCard's `hasDuration`
 * guard hides the pill.
 */
export function formatMediaDuration(
	seconds: number | null | undefined,
): string {
	if (seconds == null || seconds <= 0) return "—";
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${String(s).padStart(2, "0")}`;
}

export function mapArticleToCard(
	a: EducationHubArticleSummary,
): EducationContent {
	const contentType: ContentType = a.hasVideo
		? "video"
		: a.hasPodcast
			? "podcast"
			: "article";
	const availableFormats: ContentFormat[] = ["text", "cards"];
	if (a.hasPodcast) availableFormats.push("audio");
	if (a.hasVideo) availableFormats.push("video");
	return {
		id: a.id,
		title: a.title,
		description:
			a.personaMode === "TWO_EXPERTS"
				? "Two-expert deep dive — listen or read at your own pace."
				: "A learner-first guide tailored to your fertility journey.",
		// i18n key for the synthesized blurb above (the English text is the
		// fallback); ContentCard resolves it in the user's language.
		descriptionKey:
			a.personaMode === "TWO_EXPERTS"
				? "recommended.descriptionExperts"
				: "recommended.descriptionLearner",
		thumbnail: "/images/education/placeholder-1.svg",
		contentType,
		duration: formatMediaDuration(a.durationSeconds),
		availableFormats,
		isBookmarked: false,
		imageUrl: a.imageUrl ?? null,
	};
}

export interface EducationContent {
	id: string;
	title: string;
	description: string;
	/** education-namespace key for `description`; the string is its fallback. */
	descriptionKey?: string;
	thumbnail: string;
	contentType: ContentType;
	duration: string;
	availableFormats: ContentFormat[];
	isBookmarked: boolean;
	progress?: number;
	currentTime?: string;
	imageUrl?: string | null;
}

export interface TrainingItem {
	id: string;
	title: string;
	/** education-namespace key for `title`; the English title is its fallback. */
	titleKey?: string;
	contentType: ContentType;
	duration: string;
	isCompleted: boolean;
}

export interface CommunityGroup {
	id: string;
	name: string;
	/** education-namespace key for `name`; the English name is its fallback. */
	nameKey?: string;
	iconColor: string;
	discussionCount: number;
}

export const MOCK_RECOMMENDED: EducationContent[] = [
	{
		id: "rec-1",
		title: "Understanding IVF: A Complete Guide",
		description:
			"Learn about the IVF process from start to finish, including what to expect at each stage.",
		thumbnail: "/images/education/placeholder-1.svg",
		contentType: "video",
		duration: "10:00",
		availableFormats: ["video", "audio", "text"],
		isBookmarked: false,
	},
	{
		id: "rec-2",
		title: "Nutrition During Fertility Treatment",
		description:
			"Discover the best dietary practices to support your fertility journey and overall well-being.",
		thumbnail: "/images/education/placeholder-2.svg",
		contentType: "article",
		duration: "8:00",
		availableFormats: ["video", "audio", "text"],
		isBookmarked: true,
	},
	{
		id: "rec-3",
		title: "Managing Stress & Anxiety",
		description:
			"Practical techniques for managing stress and anxiety during your fertility treatment.",
		thumbnail: "/images/education/placeholder-3.svg",
		contentType: "podcast",
		duration: "15:00",
		availableFormats: ["video", "audio", "text"],
		isBookmarked: false,
	},
];

/**
 * Recommendations fallback used ONLY in local dev. In deployed builds
 * (staging/production) this is empty, so users never see placeholder cards whose
 * ids (rec-1…) 404 against the real API — the section stays empty until the
 * education backend (content-prism) returns real content. `import.meta.env.DEV`
 * is statically replaced, so MOCK_RECOMMENDED tree-shakes out of prod bundles.
 */
export const RECOMMENDED_FALLBACK: EducationContent[] = import.meta.env.DEV
	? MOCK_RECOMMENDED
	: [];

export const MOCK_TRAINING: TrainingItem[] = [
	{
		id: "train-1",
		title: "Medication Self-Injection Tutorial",
		titleKey: "training.items.selfInjection",
		contentType: "video",
		duration: "12:00",
		isCompleted: true,
	},
	{
		id: "train-2",
		title: "Pre-Procedure Preparation",
		titleKey: "training.items.preProcedure",
		contentType: "article",
		duration: "5:00",
		isCompleted: true,
	},
	{
		id: "train-3",
		title: "Understanding Your Lab Results",
		titleKey: "training.items.labResults",
		contentType: "video",
		duration: "8:00",
		isCompleted: false,
	},
	{
		id: "train-4",
		title: "Post-Procedure Care Guide",
		titleKey: "training.items.postProcedure",
		contentType: "article",
		duration: "6:00",
		isCompleted: false,
	},
];

export const MOCK_RESUME: EducationContent = {
	id: "resume-1",
	title: "Understanding IVF: A Complete Guide",
	description:
		"Learn about the IVF process from start to finish, including what to expect at each stage.",
	thumbnail: "/images/education/placeholder-1.svg",
	contentType: "video",
	duration: "10:00",
	availableFormats: ["video", "audio", "text"],
	isBookmarked: false,
	progress: 0.342,
	currentTime: "3:24",
};

export interface ResumeItem {
	id: string;
	title: string;
	contentType: "podcast" | "video";
	progress: number; // 0..1
	currentTime: number; // seconds — resume playback position
	imageUrl: string | null; // hero image (resolve via resolveMediaUrl)
	durationSeconds: number | null; // total length; null → derive from progress
}

export const MOCK_COMMUNITY_GROUPS: CommunityGroup[] = [
	{
		id: "group-1",
		name: "IVF Support Circle",
		nameKey: "groups.items.ivfSupport",
		iconColor: "#5E60BF",
		discussionCount: 24,
	},
	{
		id: "group-2",
		name: "Nutrition & Wellness",
		nameKey: "groups.items.nutritionWellness",
		iconColor: "#E45BC2",
		discussionCount: 18,
	},
	{
		id: "group-3",
		name: "Two-Week Wait",
		nameKey: "groups.items.twoWeekWait",
		iconColor: "#FF8C00",
		discussionCount: 31,
	},
];

export function getTrainingProgress(items: TrainingItem[]): {
	completed: number;
	total: number;
} {
	return {
		completed: items.filter((item) => item.isCompleted).length,
		total: items.length,
	};
}

// --- Real prism Article / CardDeck contracts (prism/shared/schemas.py) ---

export interface ArticleAuthor {
	name: string;
	title: string | null;
}
export interface ArticleImageRef {
	url: string;
	alt: string;
	caption: string | null;
	source: "extracted" | "generated";
}

export interface ParagraphBlock {
	type: "paragraph";
	text: string;
}
export interface HeadingBlock {
	type: "heading";
	level: number;
	text: string;
}
export type CalloutKind = "key-point" | "success" | "tips" | "remember";
export interface CalloutBlock {
	type: "callout";
	kind: CalloutKind;
	title: string;
	body: string;
	items?: string[] | null;
}
export interface ProcessStep {
	number: number;
	title: string;
	body: string;
}
export interface ProcessStepsBlock {
	type: "process-steps";
	intro?: string | null;
	steps: ProcessStep[];
}
export interface ArticleImageBlock {
	type: "image";
	image: ArticleImageRef;
}

export type ArticleBlock =
	| ParagraphBlock
	| HeadingBlock
	| CalloutBlock
	| ProcessStepsBlock
	| ArticleImageBlock;

export interface Article {
	title: string;
	read_time_minutes: number;
	author: ArticleAuthor | null;
	hero_image: ArticleImageRef | null;
	intro: string;
	blocks: ArticleBlock[];
	disclaimer: string;
	source_id: string;
}

export interface DeckCard {
	number: number;
	question: string;
	answer: string;
	related_article_id: string;
}
export interface CardDeck {
	title: string;
	context: string;
	cards: DeckCard[];
	source_id: string;
}

export function isCallout(b: ArticleBlock): b is CalloutBlock {
	return b.type === "callout";
}

async function fetchJsonArtefact<T>(url: string): Promise<T> {
	if (url.startsWith("/")) {
		const { data } = await api.get<T>(url);
		return data;
	}
	const r = await fetch(url);
	if (!r.ok) {
		// Strip the query string before putting the URL in the error: presigned
		// S3 URLs carry the AWS signature in the query, and callers log this
		// error to console + Sentry — don't leak the signature.
		throw new Error(`fetch ${url.split("?")[0]} -> ${r.status}`);
	}
	return (await r.json()) as T;
}

/**
 * Resolve a media URL into something a native <audio>/<video> element can load.
 *
 * In LocalStack/proxy mode the detail endpoint hands back a relative,
 * auth-gated path (`/api/education-hub/.../asset/podcast`). Native media
 * elements can't attach the Auth0 bearer, so the raw path 401s. We fetch the
 * bytes through axios (token attached) and hand back an object URL. In real-AWS
 * mode the URL is an absolute, self-authenticating presigned S3 URL — returned
 * as-is for true streaming. Caller MUST revokeObjectURL on cleanup for blobs.
 */
export async function resolveMediaUrl(url: string): Promise<string> {
	if (url.startsWith("/")) {
		const { data } = await api.get<Blob>(url, { responseType: "blob" });
		return URL.createObjectURL(data);
	}
	return url;
}

export async function fetchArticle(url: string): Promise<Article> {
	return fetchJsonArtefact<Article>(url);
}
export async function fetchCardDeck(url: string): Promise<CardDeck> {
	return fetchJsonArtefact<CardDeck>(url);
}
